import { useState, useEffect } from "react";
import { companiesAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Server, Lock, User, Send, Loader2, CheckCircle, AlertCircle, Building2, Key, Shield } from "lucide-react";

export default function EmailSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.active_company_id || "");
  const [settings, setSettings] = useState({
    email_method: "smtp",
    // SMTP Settings
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    smtp_use_ssl: false,
    // OAuth Settings
    oauth_provider: "",
    oauth_tenant_id: "",  // Microsoft Tenant ID
    oauth_client_id: "",
    oauth_client_secret: "",
    oauth_refresh_token: "",  // Only for Gmail
    // Common Settings
    from_email: "",
    from_name: ""
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.list();
      setCompanies(response.data);
      // Auto-select if user has active company or only one company
      if (user?.active_company_id) {
        setSelectedCompanyId(user.active_company_id);
      } else if (response.data.length === 1) {
        setSelectedCompanyId(response.data[0].id);
      } else if (response.data.length > 0 && user?.role === "superadmin") {
        // For superadmin, select first company by default
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load companies:", error);
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    if (!selectedCompanyId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await companiesAPI.getEmailSettings(selectedCompanyId);
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error("Failed to load email settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companiesAPI.updateEmailSettings(selectedCompanyId, settings);
      toast.success("E-Mail-Einstellungen gespeichert");
    } catch (error) {
      console.error("Failed to save email settings:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await companiesAPI.testEmail(selectedCompanyId);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to test email:", error);
      toast.error(error.response?.data?.detail || "Test-E-Mail fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const isSmtpConfigured = settings.smtp_host && settings.from_email;
  
  // Microsoft: Needs tenant_id, client_id, client_secret (no refresh token)
  // Gmail: Needs client_id, client_secret, refresh_token
  const isMicrosoftOAuthConfigured = settings.oauth_provider === "microsoft" && 
    settings.oauth_tenant_id && settings.oauth_client_id && settings.oauth_client_secret && settings.from_email;
  const isGmailOAuthConfigured = settings.oauth_provider === "gmail" && 
    settings.oauth_client_id && settings.oauth_client_secret && settings.oauth_refresh_token && settings.from_email;
  const isOAuthConfigured = isMicrosoftOAuthConfigured || isGmailOAuthConfigured;
  
  const isConfigured = settings.email_method === "smtp" ? isSmtpConfigured : isOAuthConfigured;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="email-settings">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope']">E-Mail-Einstellungen</h1>
        <p className="text-muted-foreground">
          Konfigurieren Sie SMTP oder OAuth für den E-Mail-Versand
        </p>
      </div>

      {/* Company Selector for Superadmin */}
      {companies.length > 1 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <Label htmlFor="company-select" className="text-sm font-medium">Firma auswählen</Label>
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger className="mt-1" data-testid="company-select">
                    <SelectValue placeholder="Firma auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedCompanyId ? (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Keine Firma ausgewählt</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Bitte wählen Sie eine Firma aus, um die E-Mail-Einstellungen zu konfigurieren.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Status Banner */}
          <Card className={isConfigured ? "border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800" : "border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800"}>
            <CardContent className="flex items-center gap-3 py-4">
              {isConfigured ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-200">
                      E-Mail konfiguriert für {selectedCompany?.name} ({settings.email_method === "oauth" ? "OAuth" : "SMTP"})
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Sie können Kaufverträge und Auftragsbestätigungen per E-Mail versenden.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">E-Mail nicht konfiguriert für {selectedCompany?.name}</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Bitte konfigurieren Sie SMTP oder OAuth, um E-Mails versenden zu können.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Email Method Tabs */}
          <Tabs value={settings.email_method} onValueChange={(value) => setSettings({ ...settings, email_method: value })}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="smtp" className="flex items-center gap-2" data-testid="smtp-tab">
                <Server className="h-4 w-4" />
                SMTP
              </TabsTrigger>
              <TabsTrigger value="oauth" className="flex items-center gap-2" data-testid="oauth-tab">
                <Shield className="h-4 w-4" />
                OAuth 2.0
              </TabsTrigger>
            </TabsList>

            {/* SMTP Settings */}
            <TabsContent value="smtp" className="space-y-6 mt-6">
              {/* SMTP Server Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    SMTP-Server
                  </CardTitle>
                  <CardDescription>
                    Die Verbindungsdaten zu Ihrem E-Mail-Server
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_host">SMTP-Server *</Label>
                      <Input
                        id="smtp_host"
                        placeholder="z.B. smtp.gmail.com oder mail.example.de"
                        value={settings.smtp_host}
                        onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_port">Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        placeholder="587"
                        value={settings.smtp_port}
                        onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-6 pt-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="smtp_use_tls"
                        checked={settings.smtp_use_tls}
                        onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked, smtp_use_ssl: checked ? false : settings.smtp_use_ssl })}
                        data-testid="smtp-tls-switch"
                      />
                      <Label htmlFor="smtp_use_tls">STARTTLS verwenden (empfohlen für Port 587)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="smtp_use_ssl"
                        checked={settings.smtp_use_ssl}
                        onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_ssl: checked, smtp_use_tls: checked ? false : settings.smtp_use_tls })}
                        data-testid="smtp-ssl-switch"
                      />
                      <Label htmlFor="smtp_use_ssl">SSL/TLS verwenden (für Port 465)</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SMTP Authentication */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Authentifizierung
                  </CardTitle>
                  <CardDescription>
                    Anmeldedaten für Ihren E-Mail-Server
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_username">Benutzername</Label>
                      <Input
                        id="smtp_username"
                        placeholder="Ihre E-Mail-Adresse oder Benutzername"
                        value={settings.smtp_username}
                        onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_password">Passwort</Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        placeholder="App-Passwort oder E-Mail-Passwort"
                        value={settings.smtp_password}
                        onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                        data-testid="smtp-password-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Tipp: Bei Gmail benötigen Sie ein App-Passwort (Einstellungen → Sicherheit → 2FA → App-Passwörter)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Help Section for SMTP */}
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-sm">Häufig verwendete SMTP-Server</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="font-medium">Gmail</p>
                      <p className="text-muted-foreground">smtp.gmail.com : 587 (TLS)</p>
                    </div>
                    <div>
                      <p className="font-medium">Microsoft 365</p>
                      <p className="text-muted-foreground">smtp.office365.com : 587 (TLS)</p>
                    </div>
                    <div>
                      <p className="font-medium">IONOS / 1&1</p>
                      <p className="text-muted-foreground">smtp.ionos.de : 587 (TLS)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* OAuth Settings */}
            <TabsContent value="oauth" className="space-y-6 mt-6">
              {/* OAuth Provider Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    OAuth 2.0 Provider
                  </CardTitle>
                  <CardDescription>
                    Wählen Sie Ihren E-Mail-Provider für OAuth-Authentifizierung
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="oauth_provider">Provider *</Label>
                    <Select 
                      value={settings.oauth_provider} 
                      onValueChange={(value) => setSettings({ ...settings, oauth_provider: value })}
                    >
                      <SelectTrigger data-testid="oauth-provider-select">
                        <SelectValue placeholder="Provider auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gmail">Google Gmail</SelectItem>
                        <SelectItem value="microsoft">Microsoft 365 / Outlook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {settings.oauth_provider && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Hinweis:</strong> Um OAuth zu nutzen, benötigen Sie:
                      </p>
                      <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside mt-2 space-y-1">
                        {settings.oauth_provider === "gmail" ? (
                          <>
                            <li>Ein Google Cloud Projekt mit aktivierter Gmail API</li>
                            <li>OAuth 2.0 Credentials (Client ID und Client Secret)</li>
                            <li>Einen Refresh Token (über OAuth Consent Flow)</li>
                          </>
                        ) : (
                          <>
                            <li>Eine Azure App Registration mit <strong>Mail.Send</strong> Application Permission</li>
                            <li>Tenant ID, Application (Client) ID und Client Secret</li>
                            <li>Admin Consent für die Mail.Send Berechtigung</li>
                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* OAuth Credentials */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    OAuth Credentials
                  </CardTitle>
                  <CardDescription>
                    Die Zugangsdaten aus Ihrer {settings.oauth_provider === "gmail" ? "Google Cloud Console" : "Azure App Registration"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Microsoft: Tenant ID */}
                  {settings.oauth_provider === "microsoft" && (
                    <div className="space-y-2">
                      <Label htmlFor="oauth_tenant_id">Tenant ID *</Label>
                      <Input
                        id="oauth_tenant_id"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={settings.oauth_tenant_id}
                        onChange={(e) => setSettings({ ...settings, oauth_tenant_id: e.target.value })}
                        data-testid="oauth-tenant-id-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Die Verzeichnis-ID (Tenant ID) aus Azure Active Directory
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="oauth_client_id">Client ID *</Label>
                      <Input
                        id="oauth_client_id"
                        placeholder={settings.oauth_provider === "gmail" ? "xxx.apps.googleusercontent.com" : "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
                        value={settings.oauth_client_id}
                        onChange={(e) => setSettings({ ...settings, oauth_client_id: e.target.value })}
                        data-testid="oauth-client-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oauth_client_secret">Client Secret *</Label>
                      <Input
                        id="oauth_client_secret"
                        type="password"
                        placeholder="Client Secret"
                        value={settings.oauth_client_secret}
                        onChange={(e) => setSettings({ ...settings, oauth_client_secret: e.target.value })}
                        data-testid="oauth-client-secret-input"
                      />
                    </div>
                  </div>

                  {/* Gmail only: Refresh Token */}
                  {settings.oauth_provider === "gmail" && (
                    <div className="space-y-2">
                      <Label htmlFor="oauth_refresh_token">Refresh Token *</Label>
                      <Input
                        id="oauth_refresh_token"
                        type="password"
                        placeholder="Refresh Token aus dem OAuth Consent Flow"
                        value={settings.oauth_refresh_token}
                        onChange={(e) => setSettings({ ...settings, oauth_refresh_token: e.target.value })}
                        data-testid="oauth-refresh-token-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Der Refresh Token wird benötigt, um neue Access Tokens automatisch zu generieren.
                      </p>
                    </div>
                  )}

                  {/* Microsoft Info */}
                  {settings.oauth_provider === "microsoft" && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>Client Credentials Flow:</strong> Kein Refresh Token erforderlich. 
                        Access Tokens werden automatisch mit Client ID und Secret erneuert.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* OAuth Help Section */}
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-sm">OAuth einrichten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {settings.oauth_provider === "gmail" ? (
                    <div className="space-y-2">
                      <p className="font-medium">Google Gmail OAuth einrichten:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Öffnen Sie die <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Cloud Console</a></li>
                        <li>Erstellen Sie ein neues Projekt oder wählen Sie ein bestehendes</li>
                        <li>Aktivieren Sie die Gmail API</li>
                        <li>Erstellen Sie OAuth 2.0 Credentials (Web Application)</li>
                        <li>Konfigurieren Sie den OAuth Consent Screen</li>
                        <li>Generieren Sie einen Refresh Token über den OAuth Flow</li>
                      </ol>
                    </div>
                  ) : settings.oauth_provider === "microsoft" ? (
                    <div className="space-y-2">
                      <p className="font-medium">Microsoft Graph (Client Credentials) einrichten:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Öffnen Sie das <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal → App Registrations</a></li>
                        <li>Registrieren Sie eine neue App (oder wählen Sie eine bestehende)</li>
                        <li>Notieren Sie die <strong>Application (client) ID</strong> und <strong>Directory (tenant) ID</strong></li>
                        <li>Unter "Certificates & secrets" → Erstellen Sie ein neues <strong>Client Secret</strong></li>
                        <li>Unter "API permissions" → Add permission → Microsoft Graph → <strong>Application permissions</strong></li>
                        <li>Wählen Sie <strong>Mail.Send</strong> und klicken Sie auf "Grant admin consent"</li>
                      </ol>
                      <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                        <p className="text-amber-800 dark:text-amber-200 text-xs">
                          <strong>Wichtig:</strong> Die Absender-E-Mail muss ein existierendes Postfach in Ihrem Microsoft 365 Tenant sein.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Wählen Sie einen Provider aus, um die Einrichtungsanleitung zu sehen.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Sender Information - Common for both methods */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Absender-Informationen
              </CardTitle>
              <CardDescription>
                So erscheint Ihre Firma als Absender der E-Mails
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="from_email">Absender E-Mail *</Label>
                  <Input
                    id="from_email"
                    type="email"
                    placeholder="noreply@ihre-firma.de"
                    value={settings.from_email}
                    onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                    data-testid="from-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from_name">Absender Name</Label>
                  <Input
                    id="from_name"
                    placeholder="Ihre Firma GmbH"
                    value={settings.from_name}
                    onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                    data-testid="from-name-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={handleSave}
              disabled={saving || !selectedCompanyId}
              data-testid="save-email-settings-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || !isConfigured || !selectedCompanyId}
              data-testid="test-email-btn"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Test-E-Mail senden
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
